import { DocumentId } from '@medapp/silo/firestore-app'
import type { User } from './appUserTypes'
import type { Timestamp } from 'firebase/firestore'
import type { AppUserPausedEvent } from '~/models/DomainEvents/AppUserPausedEvent'

function mapDocument(doc: User | null): User | null {
  if (!doc) return null
  return {
    ...doc,

    name: [doc?.profileDataMap?.firstName, doc?.profileDataMap?.lastName].join(
      ' ',
    ),
    birthDate: (
      doc?.profileDataMap?.birthDate as Timestamp | undefined
    )?.toDate(),
    sex: doc?.profileDataMap?.gender,
  } as User
}

export default {
  async getUser(
    userId: string,
    options?: {
      target?: Ref<User | null>
      signal?: AbortSignal
    },
  ) {
    const { target, signal } = options ?? {}

    const { getDocument } = useFirestore()
    return await getDocument(['AppUsers', userId], {
      signal,
      map: mapDocument,
      target,
    })
  },

  async getUserAproData(
    userId: string,
    options?: {
      target?: Ref<AproData | null>
      signal?: AbortSignal
    },
  ) {
    const { target, signal } = options ?? {}

    const { getDocument } = useFirestore()
    return await getDocument(['AppUsers', userId, 'ExternalUserData', 'AproData'], {
      signal,
      target,
    })
  },

  async sendAppUserPausedEvent(event: AppUserPausedEvent) {
    await sendDomainEvent(
      'app-users',
      'paused',
      {
        userId: event.userId,
        startDate: event.startDate,
        endDate: event.endDate,
        reason: event.reason,
        note: event.note,
      },
      event.userId,
    )
  },
  async sendAppUserUnpausedEvent(userId: string) {
    await sendDomainEvent(
      'app-users',
      'unpaused',
      {
        userId,
      },
      userId,
    )
  },

  searchAppUser(searchQuery: string) {
    const { query, getDocuments } = useFirestore()
    if (!searchQuery || searchQuery.length < 2)
      return []

    return getDocuments(query<User>('AppUsers', (q) => {
      q.or(() => {
        q.profileDataMap.firstName.isLike(searchQuery)
        q.email.isLike(searchQuery)
        q.aproPatientId.isLike(searchQuery)
        q.bsn.isLike(searchQuery);
        (q as any)[DocumentId].isLike(searchQuery)
      })
    }, {
      limit: 50,
    }), {
      map: mapDocument,
    })
  },
  async getUserByAproPatientId(aproPatientId: string) {
    const { query, getDocuments } = useFirestore()

    const users = await getDocuments(query<User>('AppUsers', (q) => {
      q.aproPatientId.is(aproPatientId)
    }, {
      limit: 1,
    }), {
      map: mapDocument,
    })

    return users[0]
  },
  async sendAppUserUpdateDeliveryIntervalEvent(userId: string, interval: number | null) {
    await sendDomainEvent(
      'app-users',
      'update-delivery-interval',
      {
        userId,
        interval,
      },
      userId,
    )
  },
}
